// src/pages/ContactPage.tsx - REFACTORED
import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send, ChevronRight, Facebook, Instagram, CheckCircle, ArrowRight } from 'lucide-react';

const BRAND_COLOR = '#D5462E'; 

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMapLoaded(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };

  // Modern Input Component: Filled style, soft transition, NO shadows
  const InputField = ({ label, id, name, type = 'text', placeholder, required, value, onChange, children, isSelect = false, rows = 1 }: any) => (
    <div className="group">
      <label htmlFor={id} className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ml-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {isSelect ? (
        <div className="relative">
          <select
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full px-5 py-4 bg-gray-100 border-0 rounded-2xl text-gray-900 font-medium focus:ring-2 focus:ring-[#D5462E]/10 focus:bg-white transition-all duration-300 appearance-none outline-none"
            style={{ minHeight: '56px' }}
          >
            {children}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-400">
            <ChevronRight className="w-5 h-5 rotate-90" />
          </div>
        </div>
      ) : rows > 1 ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={rows}
          className="w-full px-5 py-4 bg-gray-100 border-0 rounded-2xl text-gray-900 font-medium placeholder-gray-400 focus:ring-2 focus:ring-[#D5462E]/10 focus:bg-white transition-all duration-300 resize-none outline-none"
          placeholder={placeholder}
        ></textarea>
      ) : (
        <input
          type={type}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className="w-full px-5 py-4 bg-gray-100 border-0 rounded-2xl text-gray-900 font-medium placeholder-gray-400 focus:ring-2 focus:ring-[#D5462E]/10 focus:bg-white transition-all duration-300 outline-none"
          placeholder={placeholder}
        />
      )}
    </div>
  );

  // Info Card: Clean layout, circular icons, NO shadows
  const InfoCard = ({ icon: Icon, title, content }: any) => (
    <div className="flex items-start p-4 rounded-2xl transition-colors duration-300">
      <div className="flex-shrink-0 w-12 h-12 bg-red-50 border border-red-100 rounded-full flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#D5462E]" />
      </div>
      <div className="ml-5">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h3>
        <p className="text-gray-600 mt-1 leading-relaxed text-sm font-medium">{content}</p>
      </div>
    </div>
  );

  // FAQ: Clean lines, no shadows
  const FAQItem = ({ question, answer }: { question: string, answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <div className="border-b border-gray-200 last:border-b-0">
        <button 
          className="w-full flex justify-between items-center py-6 text-left focus:outline-none group"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={`text-base font-medium transition-colors duration-300 ${isOpen ? 'text-[#D5462E]' : 'text-gray-800 group-hover:text-gray-900'}`}>
            {question}
          </span>
          <div className={`p-2 rounded-full transition-all duration-300 ${isOpen ? 'bg-red-50 rotate-90' : 'bg-gray-50 border border-gray-200'}`}>
            <ChevronRight className={`h-5 w-5 transition-colors duration-300 ${isOpen ? 'text-[#D5462E]' : 'text-gray-400'}`} />
          </div>
        </button>
        <div 
          className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-48 opacity-100 pb-6' : 'max-h-0 opacity-0'}`}
        >
          <p className="text-gray-500 leading-relaxed pr-8 font-medium text-sm">{answer}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      
      {/* Hero Section (Minimalist & Clean) */}
      <div className="relative pt-24 pb-16 lg:pt-32 lg:pb-20 flex items-center justify-center overflow-hidden">
        {/* Subtle background element (no shadow) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gray-50 blur-3xl rounded-full pointer-events-none" />
        
        <div className="relative text-center px-4 max-w-3xl mx-auto z-10">
          <span className="inline-block py-1 px-3 rounded-full bg-red-50 text-[#D5462E] text-xs font-semibold tracking-widest uppercase mb-4 border border-red-100">
            Support Client
          </span>
          <h1 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-6 tracking-tight">
            Contactez-nous
          </h1>
          <p className="text-lg md:text-xl text-gray-500 font-medium leading-relaxed">
            Une question sur une annonce ou une estimation ? Notre équipe est à votre écoute pour vous accompagner.
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Info Column (Glassmorphism Card - NO Shadows) */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 border border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900 mb-8">Nos coordonnées</h2>
              
              <div className="space-y-4">
                <InfoCard
                  icon={MapPin}
                  title="Adresse"
                  content={<>Bureau F2, Immeuble les Pyramides,<br/>Avenue d'Afrique Menzah 5, Tunis</>}
                />
                
                <InfoCard
                  icon={Phone}
                  title="Téléphone"
                  content="+216 98 221 229"
                />
                
                <InfoCard
                  icon={Mail}
                  title="Email"
                  content="contact@argusautomobile.tn"
                />
                
                <InfoCard
                  icon={Clock}
                  title="Horaires"
                  content={<>Lun - Ven: 8:30 - 15:00<br />Sam: 9:00 - 12:00</>}
                />
              </div>

              {/* Social Media - Integrated */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 px-4">Réseaux Sociaux</h3>
                <div className="flex space-x-3 px-4">
                  <a href="https://www.facebook.com/ArgusTunisie/?ref=bookmarks" className="p-3 bg-gray-50 rounded-full text-gray-500 hover:text-[#1877F2] transition-colors duration-300 border border-gray-200">
                    <Facebook className="h-6 w-6" />
                  </a>
                  <a href="https://www.instagram.com/argusautomobile.tn/" className="p-3 bg-gray-50 rounded-full text-gray-500 hover:text-[#E4405F] transition-colors duration-300 border border-gray-200">
                    <Instagram className="h-6 w-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Form Column (Glassmorphism Card - NO Shadows) */}
          <div className="lg:col-span-8">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-gray-200 h-full">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-semibold text-gray-900">Envoyer un message</h2>
                <div className="hidden md:flex h-10 w-10 bg-red-50 border border-red-100 rounded-full items-center justify-center">
                  <Mail className="h-5 w-5 text-[#D5462E]" />
                </div>
              </div>
              
              {isSubmitted ? (
                <div className="bg-green-50 backdrop-blur-sm border border-green-100 rounded-3xl p-8 mb-8 flex flex-col items-center text-center animate-fade-in-up">
                  <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4 border border-green-200">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Message envoyé !</h3>
                  <p className="text-gray-600 max-w-sm font-medium">
                    Merci de nous avoir contactés. Notre équipe reviendra vers vous dans les plus brefs délais.
                  </p>
                </div>
              ) : null}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Nom complet"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Ahmed Dridi"
                  />
                  
                  <InputField
                    label="Email"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Ahmed@example.com"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Téléphone"
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+216 XX XXX XXX"
                  />

                  <InputField
                    label="Sujet"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    isSelect
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="general">Question générale</option>
                    <option value="listing">Information sur une annonce</option>
                    <option value="estimation">Demande d'estimation</option>
                    <option value="partnership">Proposition de partenariat</option>
                    <option value="technical">Problème technique</option>
                    <option value="other">Autre</option>
                  </InputField>
                </div>
                
                <InputField
                  label="Message"
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Comment pouvons-nous vous aider aujourd'hui ?"
                />
                
                <div className="pt-6 flex items-center justify-between border-t border-gray-200">
                  <p className="text-xs text-gray-400 font-medium">
                    * Champs obligatoires
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`inline-flex items-center gap-2 px-8 py-4 bg-[#D5462E] text-white font-semibold rounded-full hover:bg-[#b83a24] active:scale-95 transition-all duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full"></div>
                        <span>Envoi...</span>
                      </>
                    ) : (
                      <>
                        <span>Envoyer le message</span>
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* Map & FAQ Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {/* Map Card - NO Shadows */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl overflow-hidden border border-gray-200 h-[500px] relative">
            {!mapLoaded ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-[#D5462E]"></div>
                </div>
              </div>
            ) : (
              <iframe
                src="https://maps.google.com/maps?q=immeuble+les+pyramides+menzah+5&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="L'argus Automobile de Tunisie Location"
                className="grayscale hover:grayscale-0 transition-all duration-700"
              ></iframe>
            )}
            <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md px-6 py-4 rounded-2xl border border-gray-200">
              <p className="text-sm font-semibold text-gray-900">Nous trouver</p>
              <p className="text-xs text-gray-500 mt-1 font-medium">Immeuble les Pyramides, Menzah 5</p>
            </div>
          </div>

          {/* FAQ Card - NO Shadows */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-8">Questions fréquentes</h2>
            <div className="space-y-2">
              <FAQItem
                question="Comment estimer mon véhicule ?"
                answer="Notre outil d'estimation en ligne utilise une IA avancée pour analyser le marché tunisien en temps réel. Rendez-vous dans la section 'Estimation', entrez les détails de votre véhicule, et obtenez un prix précis en quelques secondes."
              />
              <FAQItem
                question="Délai de publication d'une annonce ?"
                answer="Pour garantir la qualité de notre plateforme, chaque annonce est vérifiée manuellement. Ce processus prend généralement moins de 24 heures ouvrables. Vous serez notifié par email dès la validation."
              />
              <FAQItem
                question="Modifier une annonce existante ?"
                answer="Pour l'instant, les modifications se font via le support pour des raisons de sécurité. Envoyez-nous un message via ce formulaire avec votre numéro de référence et les changements souhaités."
              />
              <FAQItem
                question="Tarifs de publication ?"
                answer="L'Argus Tunisie propose des formules gratuites et premium. La formule standard est 100% gratuite. Les options de mise en avant sont payantes pour maximiser votre visibilité."
              />
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
}